import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import ImageGallery from 'react-image-gallery';
import { useIntl } from 'react-intl';
import { Container } from 'reactstrap';

import { getFilePlaceholderElement } from '../../../../helpers/PageSections';
import {
  usePageAllowedCollectionUuids,
  usePageId,
  usePageIsEditing,
} from '../../../../helpers/PageIdContext';

import { withLayout } from '../PuckLayout';
import formatIntl from '../Common/formatIntl';
import SectionHeader from '../Common/SectionHeader';
import SectionHeadingField from '../Common/SectionHeadingField';
import SliderCollectionPicker from './CollectionPicker';
import Loading from '../../../Loading';
import { useLiveCollection } from './useLiveCollection';

const buildSliderPlaceholders = (count = 3) => Array.from({ length: count }, (_, index) => ({
  isPlaceholder: true,
  original: '__placeholder__',
  thumbnail: '__placeholder__',
  originalAlt: `placeholder-${index}`,
  thumbnailAlt: `placeholder-${index}`,
}));

const renderSliderItem = (item) => {
  if (item?.isPlaceholder || !item?.original) {
    return getFilePlaceholderElement({ className: 'puck-slider-file-placeholder' });
  }

  return (
    <img
      className="image-gallery-image"
      src={item.original}
      alt={item.originalAlt || ''}
    />
  );
};

const renderSliderThumbInner = (item) => {
  if (item?.isPlaceholder || !item?.thumbnail) {
    return getFilePlaceholderElement({ className: 'puck-slider-thumb-placeholder' });
  }

  return (
    <img
      className="image-gallery-thumbnail-image"
      src={item.thumbnail}
      alt={item.thumbnailAlt || ''}
    />
  );
};

const PuckSliderRender = ({
  collection,
  sectionHeading,
}) => {
  const intl = useIntl();
  const pageId = usePageId();
  const isEditing = usePageIsEditing();
  const allowedCollectionUuids = usePageAllowedCollectionUuids();
  const { liveCollection, loading } = useLiveCollection(
    collection,
    pageId,
    isEditing,
    allowedCollectionUuids,
  );
  const imageGallery = useRef();
  const placeholderItems = useMemo(() => buildSliderPlaceholders(3), []);
  const sectionTitle = sectionHeading?.title || '';
  const sectionSubtitle = sectionHeading?.subtitle || '';
  const headerAlign = sectionHeading?.align || 'left';

  const header = (
    <SectionHeader
      title={sectionTitle}
      subtitle={sectionSubtitle}
      align={headerAlign}
    />
  );

  const { files: liveFiles } = liveCollection || {};
  const files = useMemo(() => {
    if (Array.isArray(liveFiles)) {
      return liveFiles;
    }

    if (Array.isArray(liveFiles?.data)) {
      return liveFiles.data;
    }

    return [];
  }, [liveFiles]);

  const photos = useMemo(() => files
    .map((file) => {
      const original = file?.urls?.featured
        || file?.urls?.original
        || file?.urls?.preview
        || file?.urls?.thumbnailx2
        || file?.urls?.thumbnail;
      const thumbnail = file?.urls?.thumbnail
        || file?.urls?.thumbnailx2
        || file?.urls?.preview
        || original;

      if (!original) {
        return null;
      }

      return {
        original,
        thumbnail,
      };
    })
    .filter(Boolean), [files]);

  if (!collection) {
    return (
      <div className="custom-page-section landing-collection-slider">
        <Container>
          {header}
          <ImageGallery
            ref={(ref) => { imageGallery.current = ref; }}
            items={placeholderItems}
            renderItem={renderSliderItem}
            renderThumbInner={renderSliderThumbInner}
            showPlayButton={false}
            showFullscreenButton={false}
            showThumbnails={true}
          />
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="custom-page-section landing-collection-slider">
        <Container>
          <Loading />
        </Container>
      </div>
    );
  }

  if (photos.length === 0) {
    if (isEditing) {
      return (
        <div className="custom-page-section landing-collection-slider">
          <Container>
            {header}
            <ImageGallery
              ref={(ref) => { imageGallery.current = ref; }}
              items={placeholderItems}
              renderItem={renderSliderItem}
              renderThumbInner={renderSliderThumbInner}
              showPlayButton={false}
              showFullscreenButton={false}
              showThumbnails={true}
            />
            <p className="center puck-component-info-text mt-2">
              {intl.formatMessage({ defaultMessage: 'Use the "Add Collection" button to add a Collection to Slider.' })}
            </p>
          </Container>
        </div>
      );
    }

    return null;
  }

  return (
    <div className="custom-page-section landing-collection-slider">
      <Container>
        {header}
        <ImageGallery
          ref={(ref) => { imageGallery.current = ref; }}
          items={photos}
          showPlayButton={false}
          showFullscreenButton={false}
          showThumbnails={photos.length > 1}
        />
      </Container>
    </div>
  );
};

PuckSliderRender.propTypes = {
  collection: PropTypes.object,
  sectionHeading: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    align: PropTypes.string,
  }),
};

PuckSliderRender.defaultProps = {
  collection: null,
  sectionHeading: null,
};

const PuckSliderInternal = {
  fields: {
    sectionHeading: {
      type: 'custom',
      get label() { return formatIntl('Section Heading'); },
      render: (props) => <SectionHeadingField {...props} />,
    },
    collection: {
      type: 'custom',
      get label() { return formatIntl('Collection'); },
      render: ({ value, onChange, field }) => (
        <SliderCollectionPicker value={value} onChange={onChange} label={field.label} />
      ),
    },
  },

  get defaultProps() {
    return {
      sectionHeading: {
        title: formatIntl('Header'),
        subtitle: formatIntl('Subheader'),
        align: 'left',
      },
      collection: null,
      layout: {
        padding: '0px',
      },
    };
  },

  render: (props) => <PuckSliderRender {...props} />,
};

export default withLayout(PuckSliderInternal);
